import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLegacyChapterCache } from './chapter-cache';
import {
  DocReaderContext,
  type DocReaderProps,
  type FetchChaptersFnParams,
} from './core';

export type { DocReaderViewHandle, FetchChaptersFn } from './core';

export function DocReaderProvider({
  children,
  logSettings,
  ...props
}: DocReaderProps) {
  const [logger] = useState(() =>
    logSettings.parentLogger.child(
      { module: 'DocReader' },
      logSettings.logOptions,
    ),
  );

  const wrappedFetchChaptersFn = useCallback(
    async (opts: FetchChaptersFnParams) => {
      logger.info({ opts }, 'Fetching chapters chunk');
      try {
        return await props.fetchChapters(opts);
      } catch (error) {
        logger.error({ error }, 'Error fetching chapters chunk');
        throw error;
      }
    },
    [logger, props],
  );
  const { cachedChunks, paragraphCounts } = useLegacyChapterCache(
    props.documentSummary,
  );
  const docReaderContext = useMemo(
    () => ({
      ...props,
      fetchChapters: wrappedFetchChaptersFn,
      logger,
      cachedChunks,
      paragraphCounts,
    }),
    [cachedChunks, logger, paragraphCounts, props, wrappedFetchChaptersFn],
  );

  useEffect(() => {
    logger.info(
      {
        audioId: props.documentSummary.id,
        chaptersLength: props.documentSummary.chapters.length,
      },
      'Initialized',
    );
  }, [logger, props]);

  return (
    <DocReaderContext value={docReaderContext}>{children}</DocReaderContext>
  );
}
